/**
 * roc.ts — Section 5: "ROC and AUC — One Curve for Every Threshold at Once".
 *
 * Two side-by-side canvases:
 *
 *   - Left:  the score distributions (negatives blue, positives green) with a
 *            movable threshold. The "Separation" slider shifts the positive
 *            class's mean; the "Sweep threshold" button animates the threshold
 *            from 1 → 0 and back.
 *   - Right: the ROC curve in (FPR, TPR) space. Every position of the left
 *            threshold maps to one point on the curve; as the threshold sweeps,
 *            a marker traces the curve from (0,0) to (1,1). AUC is computed by
 *            trapezoidal integration of the full curve and shown as a readout.
 *
 * The point of the demo: AUC is *threshold-independent*. Two models with the
 * same accuracy at one threshold can have wildly different AUCs — the one
 * whose curve hugs the top-left corner separates the classes better *everywhere*.
 */

import { classificationScores, confusion, type ClassSample } from './data';

const N_BUCKETS = 30; // finer than Sections 3/4 — ROC needs smooth curves

export function initROC(): void {
  const distCanvas = document.getElementById('rocDistCanvas') as HTMLCanvasElement | null;
  const rocCanvas = document.getElementById('rocCurveCanvas') as HTMLCanvasElement | null;
  const sepSlider = document.getElementById('rocSepSlider') as HTMLInputElement | null;
  const sepValue = document.getElementById('rocSepValue');
  const sweepBtn = document.getElementById('rocSweepBtn') as HTMLButtonElement | null;
  const thrReadout = document.getElementById('rocThresholdValue');
  const aucValue = document.getElementById('rocAucValue');
  const statusEl = document.getElementById('rocStatus');
  if (
    !distCanvas || !rocCanvas || !sepSlider || !sepValue || !sweepBtn ||
    !thrReadout || !aucValue || !statusEl
  ) return;

  const distCtx = distCanvas.getContext('2d');
  const rocCtx = rocCanvas.getContext('2d');
  if (!distCtx || !rocCtx) return;

  const DW = distCanvas.width;
  const DH = distCanvas.height;
  const RW = rocCanvas.width;
  const RH = rocCanvas.height;
  const PAD = 36;

  const mapDistX = (s: number): number => PAD + s * (DW - 2 * PAD);
  const mapRocX = (fpr: number): number => PAD + fpr * (RW - 2 * PAD);
  const mapRocY = (tpr: number): number => RH - PAD - tpr * (RH - 2 * PAD);

  // Mutable state: separation shifts the positive class; threshold is the
  // current marker; animating gates the sweep.
  const state: {
    separation: number;
    threshold: number;
    animating: boolean;
    animDir: 1 | -1;
  } = {
    separation: parseFloat(sepSlider.value),
    threshold: 0.5,
    animating: false,
    animDir: -1,
  };

  // Recompute samples whenever separation changes. We cache so a single slider
  // tick doesn't rebuild 120 samples mid-animation.
  let cachedSamples: ClassSample[] = classificationScores(state.separation);
  function refreshSamples(): void {
    cachedSamples = classificationScores(state.separation);
  }

  // --- ROC curve computation ----------------------------------------------
  // Sweep the threshold over every unique score, compute (FPR, TPR) at each,
  // and return the ordered list of points. AUC = trapezoidal area under it.
  function rocCurve(samples: ClassSample[]): { fpr: number; tpr: number }[] {
    const pos = samples.filter((s) => s.label === 1).length;
    const neg = samples.length - pos;
    // Thresholds: every distinct score, plus the two sentinels (above max, below min).
    const thresholds = Array.from(new Set(samples.map((s) => s.score))).sort((a, b) => a - b);
    const points: { fpr: number; tpr: number }[] = [];
    // Sentinel at threshold = +∞: everything predicted negative.
    points.push({ fpr: 0, tpr: 0 });
    for (const t of thresholds) {
      const c = confusion(samples, t);
      points.push({
        fpr: neg === 0 ? 0 : c.fp / neg,
        tpr: pos === 0 ? 0 : c.tp / pos,
      });
    }
    // Sentinel at threshold = -∞: everything predicted positive.
    points.push({ fpr: 1, tpr: 1 });
    // Sort by FPR ascending, TPR ascending, so the polyline is monotonic.
    points.sort((a, b) => a.fpr - b.fpr || a.tpr - b.tpr);
    return points;
  }

  function aucFromCurve(curve: { fpr: number; tpr: number }[]): number {
    let area = 0;
    for (let i = 1; i < curve.length; i++) {
      const dx = curve[i].fpr - curve[i - 1].fpr;
      area += (dx * (curve[i].tpr + curve[i - 1].tpr)) / 2;
    }
    return area;
  }

  // Find the (FPR, TPR) point on the curve that corresponds to the current
  // threshold value. We recompute confusion at exactly `threshold` — this is
  // the marker, which may sit slightly off the polyline (a tie at adjacent
  // scores), but visually it tracks the sweep faithfully.
  function markerPoint(samples: ClassSample[], threshold: number): { fpr: number; tpr: number } {
    const c = confusion(samples, threshold);
    const pos = samples.filter((s) => s.label === 1).length;
    const neg = samples.length - pos;
    return {
      fpr: neg === 0 ? 0 : c.fp / neg,
      tpr: pos === 0 ? 0 : c.tp / pos,
    };
  }

  // --- Drawing -------------------------------------------------------------
  function bucketize(scores: number[]): number[] {
    const counts = new Array(N_BUCKETS).fill(0);
    for (const s of scores) {
      const b = Math.min(N_BUCKETS - 1, Math.max(0, Math.floor(s * N_BUCKETS)));
      counts[b]++;
    }
    return counts;
  }

  function drawDists(): void {
    const negScores = cachedSamples.filter((s) => s.label === 0).map((s) => s.score);
    const posScores = cachedSamples.filter((s) => s.label === 1).map((s) => s.score);
    const negB = bucketize(negScores);
    const posB = bucketize(posScores);
    const maxCount = Math.max(...negB, ...posB, 1);

    distCtx!.clearRect(0, 0, DW, DH);
    // Light gridlines.
    distCtx!.strokeStyle = '#e2e8f0';
    distCtx!.lineWidth = 1;
    distCtx!.beginPath();
    for (let i = 0; i <= 10; i++) {
      const x = mapDistX(i / 10);
      distCtx!.moveTo(x, PAD);
      distCtx!.lineTo(x, DH - PAD);
    }
    distCtx!.stroke();

    const bucketW = (DW - 2 * PAD) / N_BUCKETS;
    const barH = (count: number): number => (count / maxCount) * (DH - 2 * PAD);

    distCtx!.fillStyle = 'rgba(76, 114, 176, 0.55)';
    for (let i = 0; i < N_BUCKETS; i++) {
      const h = barH(negB[i]);
      distCtx!.fillRect(PAD + i * bucketW, DH - PAD - h, bucketW - 1, h);
    }
    distCtx!.fillStyle = 'rgba(85, 168, 104, 0.55)';
    for (let i = 0; i < N_BUCKETS; i++) {
      const h = barH(posB[i]);
      distCtx!.fillRect(PAD + i * bucketW, DH - PAD - h, bucketW - 1, h);
    }

    // Threshold marker.
    distCtx!.strokeStyle = '#c44e52';
    distCtx!.lineWidth = 2.5;
    distCtx!.setLineDash([6, 3]);
    distCtx!.beginPath();
    distCtx!.moveTo(mapDistX(state.threshold), PAD);
    distCtx!.lineTo(mapDistX(state.threshold), DH - PAD);
    distCtx!.stroke();
    distCtx!.setLineDash([]);
  }

  function drawROC(curve: { fpr: number; tpr: number }[], marker: { fpr: number; tpr: number }, auc: number): void {
    rocCtx!.clearRect(0, 0, RW, RH);

    // Axes.
    rocCtx!.strokeStyle = '#cbd5e1';
    rocCtx!.lineWidth = 1;
    rocCtx!.beginPath();
    rocCtx!.moveTo(PAD, PAD);
    rocCtx!.lineTo(PAD, RH - PAD);
    rocCtx!.lineTo(RW - PAD, RH - PAD);
    rocCtx!.stroke();
    // Diagonal reference (random classifier).
    rocCtx!.strokeStyle = '#e2e8f0';
    rocCtx!.setLineDash([3, 3]);
    rocCtx!.beginPath();
    rocCtx!.moveTo(mapRocX(0), mapRocY(0));
    rocCtx!.lineTo(mapRocX(1), mapRocY(1));
    rocCtx!.stroke();
    rocCtx!.setLineDash([]);

    // Filled area under the curve — visual AUC.
    rocCtx!.fillStyle = 'rgba(76, 114, 176, 0.15)';
    rocCtx!.beginPath();
    rocCtx!.moveTo(mapRocX(0), mapRocY(0));
    for (const pt of curve) rocCtx!.lineTo(mapRocX(pt.fpr), mapRocY(pt.tpr));
    rocCtx!.lineTo(mapRocX(1), mapRocY(0));
    rocCtx!.closePath();
    rocCtx!.fill();

    // The curve itself.
    rocCtx!.strokeStyle = '#4c72b0';
    rocCtx!.lineWidth = 2.5;
    rocCtx!.beginPath();
    rocCtx!.moveTo(mapRocX(curve[0].fpr), mapRocY(curve[0].tpr));
    for (const pt of curve) rocCtx!.lineTo(mapRocX(pt.fpr), mapRocY(pt.tpr));
    rocCtx!.stroke();

    // Marker — the (FPR, TPR) for the current threshold on the left canvas.
    rocCtx!.fillStyle = '#c44e52';
    rocCtx!.beginPath();
    rocCtx!.arc(mapRocX(marker.fpr), mapRocY(marker.tpr), 6, 0, Math.PI * 2);
    rocCtx!.fill();

    // Readouts.
    thrReadout!.textContent = state.threshold.toFixed(2);
    aucValue!.textContent = auc.toFixed(3);

    if (auc > 0.9) {
      statusEl!.textContent = `AUC = ${auc.toFixed(2)} — near-perfect separation. The classes barely overlap, so almost every threshold works.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-emerald-600';
    } else if (auc > 0.7) {
      statusEl!.textContent = `AUC = ${auc.toFixed(2)} — useful but imperfect. The overlap zone is where errors live, no matter the threshold.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else if (auc > 0.55) {
      statusEl!.textContent = `AUC = ${auc.toFixed(2)} — barely better than a coin flip. The score carries little signal about the class.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    } else {
      statusEl!.textContent = `AUC = ${auc.toFixed(2)} — worse than random. You may have the labels flipped.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    }
  }

  function drawAll(): void {
    const curve = rocCurve(cachedSamples);
    const auc = aucFromCurve(curve);
    const marker = markerPoint(cachedSamples, state.threshold);
    drawDists();
    drawROC(curve, marker, auc);
  }

  // --- Events --------------------------------------------------------------
  sepSlider.addEventListener('input', () => {
    if (state.animating) return;
    state.separation = parseFloat(sepSlider.value);
    sepValue!.textContent = state.separation.toFixed(2);
    refreshSamples();
    drawAll();
  });

  // Sweep the threshold from current → 0 → 1 → back, tracing the ROC. Each
  // half-sweep is ~1.2 s; the marker visibly traces the curve.
  const sweepButton = sweepBtn; // non-null alias carries narrowing into frame()
  sweepButton.addEventListener('click', () => {
    if (state.animating) return;
    state.animating = true;
    sweepButton.disabled = true;
    const startThr = state.threshold;
    const target = state.animDir === -1 ? 0 : 1;
    const startTime = performance.now();
    const duration = 1200;

    function frame(t: number): void {
      const p = Math.min(1, (t - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      state.threshold = startThr + (target - startThr) * eased;
      drawAll();
      if (p < 1) {
        requestAnimationFrame(frame);
      } else {
        state.animating = false;
        state.animDir = state.animDir === -1 ? 1 : -1;
        sweepButton.disabled = false;
      }
    }
    requestAnimationFrame(frame);
  });

  sepValue!.textContent = state.separation.toFixed(2);
  drawAll();
}
